import { useEffect, useRef, useState } from "react";
import {
  formatNotificationCenterBadgeCount,
  getNotificationBadgeTone,
} from "../containers/NotificationCenter/utils/notificationCenterCounts";

/** Delay between roulette steps — higher target → faster spin (ms). */
export function getNotificationBadgeRollStepDelay(step, target) {
  if (target <= 0) {
    return 0;
  }

  const progress = step / target;
  const eased = progress ** 2.2;
  const maxDelay = Math.max(38, 96 - target * 0.85);
  const minDelay = Math.max(6, 22 - target * 0.12);

  return Math.round(maxDelay - (maxDelay - minDelay) * eased);
}

function getBadgeText(count, { isRolling, targetTotal }) {
  if (isRolling && count === 0) {
    return null;
  }

  if (!isRolling && targetTotal <= 0) {
    return null;
  }

  return formatNotificationCenterBadgeCount(count);
}

function getBadgeTone(count, { isRolling, targetTotal }) {
  if (!isRolling && targetTotal <= 0) {
    return "none";
  }

  return getNotificationBadgeTone(count);
}

/**
 * First-load roulette: 1 → 2 → … → target (99+ when target >= 99).
 * Runs once per mount after counters are ready.
 */
export default function useNotificationBadgeRoll(targetTotal, { isReady = false } = {}) {
  const resolvedTarget = Math.max(0, Number(targetTotal) || 0);
  const animationTarget = resolvedTarget >= 99 ? 99 : resolvedTarget;

  const [displayCount, setDisplayCount] = useState(0);
  const [isRolling, setIsRolling] = useState(false);
  const hasPlayedRef = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!isReady || hasPlayedRef.current) {
      return undefined;
    }

    hasPlayedRef.current = true;

    if (resolvedTarget <= 0) {
      setDisplayCount(0);
      setIsRolling(false);
      return undefined;
    }

    setDisplayCount(0);
    setIsRolling(true);

    let current = 0;

    const tick = () => {
      if (current >= animationTarget) {
        setDisplayCount(resolvedTarget);
        setIsRolling(false);
        return;
      }

      current += 1;
      setDisplayCount(current);
      timeoutRef.current = setTimeout(
        tick,
        getNotificationBadgeRollStepDelay(current, animationTarget)
      );
    };

    timeoutRef.current = setTimeout(
      tick,
      getNotificationBadgeRollStepDelay(0, animationTarget)
    );

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [animationTarget, isReady, resolvedTarget]);

  const text = getBadgeText(displayCount, {
    isRolling,
    targetTotal: resolvedTarget,
  });
  const tone = getBadgeTone(displayCount, {
    isRolling,
    targetTotal: resolvedTarget,
  });

  return {
    text,
    tone,
    isRolling,
    displayCount,
  };
}
